/**
 * File utilities — validation, naming, size formatting.
 */

// ─── Accepted types ────────────────────────────────────────────────────────────

export const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const ACCEPTED_AUDIO_TYPES = ['audio/mpeg', 'audio/mp4', 'audio/wav', 'audio/ogg'];

export const IMAGE_ACCEPT_STRING = ACCEPTED_IMAGE_TYPES.join(',');
export const AUDIO_ACCEPT_STRING = ACCEPTED_AUDIO_TYPES.join(',');

// ─── Size limits ───────────────────────────────────────────────────────────────

export const MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB
export const MAX_AUDIO_SIZE_BYTES = 50 * 1024 * 1024; // 50 MB

// ─── Validation ────────────────────────────────────────────────────────────────

export interface FileValidationResult {
  valid:   boolean;
  error:   string | null;
}

export function validateImageFile(file: File): FileValidationResult {
  if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
    return { valid: false, error: 'Please upload a JPEG, PNG, or WebP image.' };
  }
  if (file.size > MAX_IMAGE_SIZE_BYTES) {
    return { valid: false, error: `Image must be smaller than ${formatFileSize(MAX_IMAGE_SIZE_BYTES)}.` };
  }
  return { valid: true, error: null };
}

export function validateAudioFile(file: File): FileValidationResult {
  if (!ACCEPTED_AUDIO_TYPES.includes(file.type)) {
    return { valid: false, error: 'Please upload an MP3, M4A, WAV, or OGG audio file.' };
  }
  if (file.size > MAX_AUDIO_SIZE_BYTES) {
    return { valid: false, error: `Audio must be smaller than ${formatFileSize(MAX_AUDIO_SIZE_BYTES)}.` };
  }
  return { valid: true, error: null };
}

// ─── File naming ───────────────────────────────────────────────────────────────

/**
 * Sanitises a filename by removing unsafe characters.
 */
export function sanitiseFilename(filename: string): string {
  return filename
    .toLowerCase()
    .replace(/[^a-z0-9._-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * Generates a storage filename with a timestamp prefix to avoid collisions.
 * e.g. "1720900000000_my-image.jpg"
 */
export function generateStorageFilename(originalFilename: string): string {
  const timestamp = Date.now();
  const sanitised = sanitiseFilename(originalFilename);
  return `${timestamp}_${sanitised}`;
}

/**
 * Builds the Firebase Storage path for a media file.
 * e.g. storagePath("mothers", 256, "images", "1720900000000_photo.jpg")
 *   → "mothers/day-256/images/1720900000000_photo.jpg"
 */
export function buildStoragePath(
  audience: string,
  dayNumber: number,
  mediaType: 'images' | 'audio',
  filename: string
): string {
  return `${audience}/day-${dayNumber}/${mediaType}/${filename}`;
}

// ─── Formatting ────────────────────────────────────────────────────────────────

/**
 * Formats a file size in bytes to a human-readable string.
 * e.g. 1048576 → "1.0 MB"
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024)        return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Gets the file extension from a filename or URL.
 * e.g. "photo.jpg" → "jpg"
 */
export function getFileExtension(filename: string): string {
  return filename.split('.').pop()?.toLowerCase() ?? '';
}
